import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import CloseButton from "../../components/Application/CloseButton";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from '../../utils/api';
import { getLocalSystemDate } from '../../utils/dateUtils';
import { buildPayloadWithIsoDates } from '../../utils/applicationFormPayload';
import { getStandardizedSignatures } from '../../utils/formHelpers';
import EvaluationFormHeader from '../EvaluationNew/components/EvaluationFormHeader';
import { governmentFormSchema } from './application.validation';

const DE4Form = ({ positionName, onClose }) => {
  const [de4Id, setDe4Id] = useState(null);


  const methods = useForm({
    resolver: zodResolver(governmentFormSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      applicantName: "",
      applicantSignature: "",
      applicantSignedAt: getLocalSystemDate(),
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: ""
    },
    mode: "onChange"
  });

  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ['applicantSignedAt', 'reviewedBySignedAt'],
        { flag: actualFlag }
      );
      payload.positionAppliedFor = positionName;

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      const method = de4Id ? 'PUT' : 'POST';
      const url = de4Id ? `/de4-forms/${de4Id}` : '/de4-forms';

      const response = await request(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.status === 1) {
        toast.success(response.message || "DE-4 Form processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process DE-4 Form");
      }
    } catch (error) {
      console.error("Error submitting DE-4:", error);
      toast.error("Failed to submit form");
    }
  };

  const {
    targetUserId,
    user: currentUserData,
    isAdmin,
    closeForm,
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });


  const { reset } = methods;

  useEffect(() => {
    const fetchData = async () => {
      if (!targetUserId || !currentUserData) return;
      try {
        const response = await request(`/de4-forms/${targetUserId}`);
        if (response.status === 1 && response.result) {
          const data = response.result.de4Form || response.result;
          setDe4Id(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            applicantName: data.applicantName || "",
            applicantSignature: data.applicantSignature || "",
            ...getStandardizedSignatures(data, isAdmin)
          });
        }
      } catch (error) {
        console.error("Error fetching DE4 data:", error);
      }
    };
    fetchData();
  }, [targetUserId, reset, currentUserData, isAdmin]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <CloseButton onClose={closeForm} />

      <div className="max-w-6xl mx-auto bg-white shadow-xl overflow-hidden">
        <EvaluationFormHeader
          title="EDD (DE-4) Form"
          subtitle="Employee's Withholding Allowance Certificate"
        />
        {/* PDF Viewer */}
        <div className="mb-8 border rounded-xl overflow-hidden  shadow-sm h-[800px] p-8">
          <iframe
            src="/DE4.pdf#toolbar=1"
            className="w-full h-full"
            title="EDD (DE-4) Form"
          />
        </div>

      </div>

    </div>
  );
};

export default DE4Form;

